import React, { useEffect, useMemo, useRef, useState } from "react";
import { BookOpen, Bot, HelpCircle, Link2, Search, Sparkles } from "lucide-react";
import WizardStepper from "./WizardStepper";
import StepWebsite from "./StepWebsite";
import StepPersonalization from "./StepPersonalization";
import StepSettings from "./StepSettings";
import StepSources from "./StepSources";
import AnalysisReview from "./AnalysisReview";
import AnalysisErrorPage from "./AnalysisErrorPage";
import CreationLoader from "./CreationLoader";
import CreationSuccess from "./CreationSuccess";
import ModalePiani, { usePianiDaProporre } from "./ModalePiani";
import WidgetPreview from "../../WidgetPreview";
import { api } from "../../../api/client";
import { useBillingUsage, useCreateBot } from "../../../hooks/useApi";
import { useTranslation } from "../../../hooks/useTranslation";
import {
  describeApiError,
  describeAnalysisError,
} from "../../../utils/apiErrors";
import { eLimiteDelPiano, restanti } from "../../../utils/limitiPiano";
import { GRATUITO } from "../../billing/piani";
import { TESTI_PREDEFINITI } from "../../widget/sezioni";
import { trackBotCreatedFromLanding } from "../../../lib/analytics";

const DRAFT_STORAGE_KEY = "pingup-onboarding-draft";

// Step del loader dinamico durante l'ANALISI (fasi reali via SSE, non a
// timer): un elenco per ramo, coerente con gli eventi emessi dal backend.
const WEBSITE_ANALYSIS_STEPS = [
  { key: "loaderCrawl", icon: Search },
  { key: "loaderIdentity", icon: Sparkles },
  { key: "loaderLinks", icon: Link2 },
  { key: "loaderBotCreated", icon: Bot },
];
const PROMPT_ANALYSIS_STEPS = [
  { key: "loaderReading", icon: Search },
  { key: "loaderIdentity", icon: Sparkles },
  { key: "loaderKnowledge", icon: BookOpen },
  { key: "loaderFaq", icon: HelpCircle },
  { key: "loaderBotCreated", icon: Bot },
];
// Mappa gli step REALI emessi dal backend (SSE) sul numero di task spuntati.
const WEBSITE_PROGRESS_OF = { crawl: 1, identity: 2, links: 3 };
const PROMPT_PROGRESS_OF = { reading: 1, identity: 2, knowledge: 3, faq: 4 };

// Dropzone della review: stesse regole di StepSources (il backend ingerisce
// i file solo come PDF; txt/md diventano sorgenti testo lette client-side).
const REVIEW_MAX_FILE_BYTES = 50 * 1024 * 1024;
const REVIEW_MAX_INLINE_TEXT = 100000;

const DEFAULT_CONFIG = {
  // Ramo automatico — URL del sito da analizzare
  websiteUrl: "",
  // Step 1 — Personalizzazione (default dal design)
  name: "",
  subtitle: "",
  welcomeMessage: "",
  logoUrl: "",
  colorMode: "brand", // "brand" | "theme"
  themeId: null,
  primaryColor: "#334155",
  secondaryColor: "#1F77B4",
  textColor: "#020617",
  // Step 2 — Impostazioni
  promptMode: "custom", // "custom" | "template"
  templateId: null,
  systemPrompt: "",
  quickReplies: ["", ""],
  // Step 3 — Sorgenti
  sources: [],
};

// I PDF contengono oggetti File non serializzabili: restano fuori dal
// draft, dopo un refresh andranno ricaricati.
const serializableConfig = (config) => ({
  ...config,
  sources: config.sources.filter((source) => source.type !== "pdf"),
});

const loadDraft = () => {
  try {
    const raw = sessionStorage.getItem(DRAFT_STORAGE_KEY);
    if (!raw) return null;
    const draft = JSON.parse(raw);
    return {
      step: [1, 2, 3].includes(draft.step) ? draft.step : 1,
      branch: draft.branch === "auto" ? "auto" : "custom",
      config: { ...DEFAULT_CONFIG, ...draft.config },
      // Bot già creato in questo giro (creazione anticipata): al refresh si
      // torna al riepilogo, NON si rianalizza (eviterebbe bot duplicati).
      createdBot: draft.createdBot || null,
      // L'analisi è già stata completata (riepilogo mostrato) anche se la
      // creazione anticipata del bot è ancora in corso o è fallita: senza
      // questo flag un refresh sul riepilogo, in quel caso, rianalizzava da
      // capo (il bot nasce comunque per davvero alla Conferma).
      reachedReview: !!draft.reachedReview,
      // Fonti proposte/aggiunte della review (i PDF non ancora aggiunti si
      // perdono: l'oggetto File non è serializzabile).
      available: Array.isArray(draft.available) ? draft.available : [],
      // Link di menu/footer trovati dall'analisi sito, non ancora aggiunti:
      // alimentano il dialog "Analizza link" anche dopo un refresh.
      discoveredLinks: Array.isArray(draft.discoveredLinks)
        ? draft.discoveredLinks
        : [],
      // Chiavi delle fonti già inviate al bot: senza, dopo un refresh il
      // percorso "Personalizza" le ricaricherebbe come nuove (duplicati).
      createdSourceKeys: Array.isArray(draft.createdSourceKeys)
        ? draft.createdSourceKeys
        : [],
    };
  } catch {
    return null;
  }
};

const clearDraft = () => sessionStorage.removeItem(DRAFT_STORAGE_KEY);

// Guardia anti doppio-avvio dell'analisi: a livello di MODULO perché lo
// StrictMode di React (dev) monta i componenti due volte e un useRef non
// sopravvive al remount → partivano due analisi e due creazioni bot.
// Si resetta solo a un refresh vero della pagina (nuovo contesto JS).
let introRunGuard = "";

/**
 * Wizard "No, preferisco partire da zero" (design Figma onboarding):
 * 3 step — Personalizzazione, Impostazioni, Sorgenti.
 * La configurazione vive qui e viene inviata alle API solo alla fine
 * (POST /bots con widget_config → upload PDF → bulk sorgenti).
 * Step e form sopravvivono al refresh via sessionStorage (per scheda).
 */
const CustomBotWizard = ({
  onComplete,
  onExit,
  initialMode = "custom",
  // Entry prompt-first (v2): descrizione attività + sito opzionale
  // (sessionStorage) e file allegati (solo in memoria).
  intro = null,
  introFiles = [],
  // Il draft (pingup-onboarding-draft) lo pulisce già questo componente
  // (clearDraft) appena il bot nasce per davvero. L'intro (pingup-onboarding-
  // intro) invece vive un livello sopra, in CreateBotPage: questo callback fa
  // sparire anche quella nello stesso istante, invece di aspettare la
  // Conferma finale — altrimenti, se l'utente abbandona dopo la creazione
  // anticipata (bot già vero a DB) senza mai confermare, l'intro resterebbe
  // in sessionStorage per sempre.
  onIntroConsumed = () => {},
}) => {
  const { t } = useTranslation();
  const createBotMutation = useCreateBot();
  const [step, setStep] = useState(() => loadDraft()?.step ?? 1);
  // "auto" = analisi sito web; "custom" = configurazione manuale a step.
  // "prompt" internamente viaggia come "auto": stesso loader/riepilogo,
  // cambia solo la fonte dell'analisi (testo invece del sito).
  const [branch, setBranch] = useState(
    () => loadDraft()?.branch ?? (initialMode === "prompt" ? "auto" : initialMode)
  );
  // "form" → step; "analyzing" → loader analisi; "review" → riepilogo AI;
  // "creating" → loader creazione; "success" → pagina finale custom
  // Bot creato in un giro precedente della stessa sessione (refresh sul
  // riepilogo): si riparte dal riepilogo, senza rianalizzare né duplicare.
  const restoredBot = loadDraft()?.createdBot || null;
  // L'analisi era già stata completata (anche se il bot non è ancora nato,
  // es. creazione anticipata ancora in volo o fallita): basta questo per
  // NON rianalizzare al refresh, la Conferma crea comunque il bot per davvero.
  const restoredReview = restoredBot || loadDraft()?.reachedReview;
  const [phase, setPhase] = useState(() => (restoredReview ? "review" : "form"));
  const [analysis, setAnalysis] = useState(() =>
    restoredReview ? { restored: true } : null
  );
  // Progresso REALE dell'analisi (0..4): crawl → identity → ai → bot creato.
  // Alimenta la checklist del loader con gli eventi SSE del backend.
  const [analysisProgress, setAnalysisProgress] = useState(0);
  // Causa specifica del fallimento dell'analisi ({ title, description }):
  // "sito irraggiungibile" e "AI in errore" non possono dire la stessa cosa.
  const [analysisError, setAnalysisError] = useState(null);
  const [createdBot, setCreatedBot] = useState(restoredBot);
  const [createError, setCreateError] = useState("");
  // Bot creato ma fonti fallite: avviso (non errore) da mostrare all'utente.
  const [sourcesWarning, setSourcesWarning] = useState(null);
  const [isConfirming, setIsConfirming] = useState(false);
  // Chi puo' comprare un piano e supera le fonti del piano vede subito i
  // piani (ModalePiani) al posto dell'avviso scritto; con «Continua con il
  // piano gratuito» l'onboarding prosegue e finisce dritto sull'assistente.
  // fuoriPiano = { motivo } finche' la scelta e' aperta.
  const propostaPiani = usePianiDaProporre();
  const [fuoriPiano, setFuoriPiano] = useState(null);
  // Le fonti in coda superano il piano? (0 = senza limite)
  const consumi = useBillingUsage();
  const oltreIlPiano = (inCoda) => {
    const max = consumi.data?.effective?.limits?.sources_max || 0;
    return max > 0 && inCoda > restanti(max, consumi.data?.usage?.sources_count);
  };
  const isCreating = phase === "creating";
  const [config, setConfig] = useState(
    () => loadDraft()?.config ?? DEFAULT_CONFIG
  );
  // Review v2 "Fonti di conoscenza": righe {id, status, source} — le fonti
  // rilevate dall'analisi arrivano come "available" e si aggiungono col "+"
  // (push immediato sul bot creato in anticipo → l'anteprima le usa davvero);
  // i file allegati nella entry arrivano già "added" (scelti dall'utente).
  const [availableSources, setAvailableSources] = useState(
    () => loadDraft()?.available ?? []
  );
  // Link di menu/footer trovati dall'analisi sito ma non auto-aggiunti:
  // popolano il dialog "Analizza link" della review (vuoto per il ramo prompt,
  // che non ha un sito da leggere).
  const [discoveredLinks, setDiscoveredLinks] = useState(
    () => loadDraft()?.discoveredLinks ?? []
  );
  const [fileErrors, setFileErrors] = useState([]);
  const [addError, setAddError] = useState("");
  const rowIdRef = useRef(0);

  useEffect(() => {
    sessionStorage.setItem(
      DRAFT_STORAGE_KEY,
      JSON.stringify({
        step,
        branch,
        config: serializableConfig(config),
        // Persistito per il refresh: si torna al riepilogo del bot già
        // creato invece di rianalizzare (e duplicare).
        createdBot: createdBot
          ? {
              _id: createdBot._id,
              widget_code: createdBot.widget_code,
              name: createdBot.name,
            }
          : null,
        // Il riepilogo è già stato raggiunto: indipendente da createdBot,
        // così un refresh mentre la creazione anticipata è ancora in volo
        // (o è fallita) torna comunque al riepilogo invece di rianalizzare.
        reachedReview: phase === "review" || !!createdBot,
        // Righe della review: i PDF non ancora aggiunti si scartano (il File
        // non sopravvive al refresh), quelli già aggiunti restano visibili.
        available: availableSources
          .filter((r) => r.source.type !== "pdf" || r.status === "added")
          .map((r) => {
            const { file: _file, ...source } = r.source;
            return {
              ...r,
              status: r.status === "adding" ? "available" : r.status,
              source,
            };
          }),
        createdSourceKeys: createdSourcesRef.current,
        discoveredLinks,
      })
    );
  }, [step, branch, phase, config, createdBot, availableSources, discoveredLinks]);

  const updateConfig = (patch) => setConfig((c) => ({ ...c, ...patch }));

  // Struttura completa attesa dal widget: il widget
  // si aspetta l'intero albero theme/position/behavior/integrations.
  // cfg parametrico: la creazione anticipata (dopo l'analisi) avviene nello
  // stesso tick dell'applyAnalysis, quando lo stato config è ancora vecchio.
  // Testi di riserva in italiano, gli stessi del widget e del server: prima
  // ogni assistente nasceva con "Type your message..." sul sito del cliente.
  const buildWidgetConfig = (cfg = config) => ({
    welcomeMessage:
      cfg.welcomeMessage.trim() || "Ciao! Come posso aiutarti?",
    subtitle: cfg.subtitle.trim() || TESTI_PREDEFINITI.subtitle,
    placeholder: TESTI_PREDEFINITI.placeholder,
    theme: {
      primaryColor: cfg.primaryColor,
      secondaryColor: cfg.secondaryColor,
      accentColor: "#0f172a",
      backgroundColor: "#ffffff",
      textColor: cfg.textColor,
      gradientEnabled: false,
      gradientDirection: 45,
      gradientColors: [cfg.primaryColor, "#8b5cf6"],
      fontFamily: "Inter, system-ui, sans-serif",
      fontSize: 14,
      borderRadius: 8,
      spacing: 16,
      logoUrl: cfg.logoUrl || "",
      logoHeight: 32,
      logoPosition: "left",
      shadowIntensity: 4,
      animationStyle: "balloon",
      darkMode: false,
    },
    position: {
      position: "bottom-right",
      offsetX: 20,
      offsetY: 20,
      zIndex: 1000,
      size: "medium",
      mobilePosition: "bottom-center",
      mobileSize: "large",
    },
    behavior: {
      autoOpen: false,
      autoOpenDelay: 3000,
      autoOpenTrigger: "time",
      scrollPercentage: 50,
      showOnPages: "all",
      excludePages: "",
      includePaths: "",
      triggers: [],
      customCSS: "",
      soundEnabled: true,
      notifications: true,
      typing: true,
      readReceipts: true,
      fileUpload: false,
      emojiPicker: true,
      quickReplies: cfg.quickReplies.filter((q) => q.trim()),
      businessHours: {
        enabled: false,
        timezone: "UTC",
        monday: { enabled: true, start: "09:00", end: "17:00" },
        tuesday: { enabled: true, start: "09:00", end: "17:00" },
        wednesday: { enabled: true, start: "09:00", end: "17:00" },
        thursday: { enabled: true, start: "09:00", end: "17:00" },
        friday: { enabled: true, start: "09:00", end: "17:00" },
        saturday: { enabled: false, start: "09:00", end: "17:00" },
        sunday: { enabled: false, start: "09:00", end: "17:00" },
      },
      offline: {
        enabled: true,
        message: t(
          "widget.offlineMessageDefault",
          "We're currently offline. Please leave a message and we'll get back to you!"
        ),
        formEnabled: true,
      },
    },
    integrations: {
      googleAnalytics: "",
      facebookPixel: "",
      customEvents: true,
      webhooks: {
        onMessage: "",
        onStart: "",
        onEnd: "",
      },
    },
  });

  // Config per la Live Preview: il widget REALE (stesso componente della
  // sezione Widget) con i testi segnaposto del design sui campi vuoti.
  // Quando il bot è già stato creato (creazione anticipata post-analisi), la
  // preview diventa VIVA: widgetCode + previewLive fanno passare i messaggi
  // dal backend vero, che risponde con le fonti inserite dall'utente.
  const previewConfig = useMemo(() => {
    const widgetConfig = buildWidgetConfig();
    return {
      ...widgetConfig,
      placeholder: t("onboardingWizard.previewInputPlaceholder"),
      title: config.name.trim() || t("onboardingWizard.previewBotName"),
      subtitle:
        config.subtitle.trim() || t("onboardingWizard.previewSubtitle"),
      welcomeMessage:
        config.welcomeMessage.trim() || t("onboardingWizard.previewWelcome"),
      behavior: {
        ...widgetConfig.behavior,
        quickReplies:
          widgetConfig.behavior.quickReplies.length > 0
            ? widgetConfig.behavior.quickReplies
            : [
                t("onboardingWizard.previewQuick1"),
                t("onboardingWizard.previewQuick2"),
              ],
      },
      ...(createdBot?.widget_code
        ? {
            widgetCode: createdBot.widget_code,
            previewLive: true,
            apiEndpoint: import.meta.env.VITE_API_BASE_URL || "/api",
          }
        : {}),
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config, t, createdBot]);

  // Upload/bulk delle fonti per un bot esistente (riusata da creazione e
  // aggiornamento post-personalizzazione). Ritorna i documenti creati (con
  // _id Mongo) così la review può poi permettere di eliminarli singolarmente.
  const pushSources = async (botId, sources) => {
    const payload = [];
    for (const source of sources) {
      if (source.type === "pdf") {
        const generated = await api.uploads.generateUrl(
          source.filename,
          "application/pdf",
          botId,
          "source"
        );
        await api.uploads.uploadFile(source.file, generated.uploadUrl);
        payload.push({
          type: "pdf",
          filename: source.filename,
          blob_path: generated.blobPath,
        });
      } else if (source.type === "text") {
        payload.push({
          type: "text",
          inline_text: source.inline_text,
          ...(source.title ? { title: source.title } : {}),
        });
      } else if (source.type === "faq") {
        payload.push({
          type: "faq",
          question: source.question,
          answer: source.answer,
        });
      } else {
        payload.push({ type: source.type, url: source.url });
      }
    }
    if (payload.length === 0) return [];
    let response;
    try {
      response = await api.sources.bulkCreate(botId, payload);
    } catch (error) {
      // Il piano era gia' pieno: non e' entrata nessuna. Si aprono i piani.
      if (eLimiteDelPiano(error)) setFuoriPiano({ motivo: null });
      throw error;
    }
    const docs = response?.sources ?? [];
    // Il server crea quelle che stanno nel piano e dice quante restano fuori:
    // chi chiama lo dice al cliente, e si aprono i piani.
    docs.oltreIlPiano = response?.over_limit || 0;
    docs.limite = response?.limit || null;
    if (docs.oltreIlPiano) {
      setFuoriPiano({
        motivo: t("onboardingWizard.sourcesOverPlan", {
          added: docs.length,
          over: docs.oltreIlPiano,
          limit: docs.limite,
        }),
      });
    }
    return docs;
  };

  // Chiave stabile di una fonte: per capire, dopo "Personalizza", quali
  // fonti sono NUOVE rispetto a quelle già inviate alla creazione anticipata.
  // Serve anche ad abbinare i documenti creati (risposta bulkCreate) alle
  // righe locali, per poterle eliminare singolarmente dalla review.
  const sourceKey = (s) =>
    s.type === "faq"
      ? `faq|${s.question}`
      : `${s.type}|${s.url || s.filename || s.title || ""}`;
  const createdSourcesRef = useRef(loadDraft()?.createdSourceKeys ?? []);

  // Abbina i documenti appena creati (con _id) alle righe di availableSources
  // che li hanno generati, così il tasto elimina può chiamare l'API giusta.
  // Porta con sé anche lo stato di ingestione (pending/processing/ready/error):
  // la review lo mostra come badge, il polling qui sotto lo tiene aggiornato.
  const attachCreatedIds = (createdDocs) => {
    if (!createdDocs || createdDocs.length === 0) return;
    const byKey = new Map(createdDocs.map((doc) => [sourceKey(doc), doc]));
    setAvailableSources((rows) =>
      rows.map((row) => {
        if (row.source._id) return row;
        const doc = byKey.get(sourceKey(row.source));
        return doc
          ? {
              ...row,
              source: { ...row.source, _id: doc._id, ingestStatus: doc.status },
            }
          : row;
      })
    );
  };

  // Le fonti create entrano in coda lato backend (pending → processing →
  // ready/error) e non sono pronte subito: senza polling la review le
  // mostrerebbe come "aggiunte" anche quando non possono ancora rispondere.
  // Stesso giro di 4s finché ce n'è almeno una non ancora in stato finale.
  useEffect(() => {
    if (!createdBot) return;
    const hasPending = availableSources.some(
      (row) =>
        row.source._id &&
        ["pending", "processing"].includes(row.source.ingestStatus)
    );
    if (!hasPending) return;

    const interval = setInterval(async () => {
      try {
        const all = await api.sources.listAll(createdBot._id);
        const byId = new Map((all || []).map((doc) => [doc._id, doc]));
        setAvailableSources((rows) =>
          rows.map((row) => {
            const doc = row.source._id && byId.get(row.source._id);
            return doc && doc.status !== row.source.ingestStatus
              ? { ...row, source: { ...row.source, ingestStatus: doc.status } }
              : row;
          })
        );
      } catch (error) {
        console.error("Aggiornamento stato fonti fallito (non bloccante):", error);
      }
    }, 4000);

    return () => clearInterval(interval);
  }, [createdBot, availableSources]);

  // Elimina una fonte già mostrata nella review: se ha già un _id (creata
  // sul backend) va rimossa anche lì, altrimenti basta toglierla in locale.
  const removeSourceFromReview = async (row) => {
    setAddError("");
    const sourceId = row.source._id;
    if (sourceId) {
      try {
        await api.sources.delete(sourceId);
      } catch (error) {
        console.error("Eliminazione fonte fallita:", error);
        setAddError(
          describeApiError(error, t) || t("onboardingWizard.kbAddError")
        );
        return;
      }
    }
    setAvailableSources((rows) => rows.filter((r) => r.id !== row.id));
    setConfig((c) => ({
      ...c,
      sources: c.sources.filter((s) => s !== row.source),
    }));
    createdSourcesRef.current = createdSourcesRef.current.filter(
      (key) => key !== sourceKey(row.source)
    );
  };

  // Creazione vera e propria: POST /bots + upload/bulk delle fonti.
  // Ritorna { bot, sourcesError }: il bot può nascere benissimo mentre le
  // fonti falliscono, e in quel caso l'utente DEVE saperlo (prima l'errore
  // finiva solo in console e la schermata di successo mentiva).
  const performCreate = async (cfg = config) => {
    const botName = cfg.name.trim();
    const newBot = await createBotMutation.mutateAsync({
      name: botName,
      description: "",
      system_prompt: cfg.systemPrompt,
      widget_config: buildWidgetConfig(cfg),
    });
    trackBotCreatedFromLanding();

    // Le fonti si creano dopo il bot (serve il botId). Se falliscono
    // il bot esiste comunque: si potranno riaggiungere dalla pagina Sorgenti.
    try {
      const createdDocs = await pushSources(newBot._id, cfg.sources);
      attachCreatedIds(createdDocs);
      createdSourcesRef.current = cfg.sources.map(sourceKey);
      if (createdDocs.oltreIlPiano) {
        return {
          bot: newBot,
          sourcesError: t("onboardingWizard.sourcesOverPlan", {
            added: createdDocs.length,
            over: createdDocs.oltreIlPiano,
            limit: createdDocs.limite,
          }),
        };
      }
    } catch (error) {
      console.error(
        "Bot creato ma alcune sorgenti non sono state aggiunte:",
        error
      );
      return {
        bot: newBot,
        sourcesError:
          describeApiError(error, t) || t("onboardingWizard.sourcesFailedReason"),
      };
    }

    return { bot: newBot, sourcesError: null };
  };

  // Creazione anticipata (v2): il bot nasce subito dopo l'analisi, così la
  // Live Preview del riepilogo è il widget VERO che risponde con le fonti
  // inserite. Se fallisce non blocca nulla: la Conferma ritenta come prima.
  // La promise resta in un ref: se l'utente conferma mentre la creazione è
  // ancora in volo, handleConfirm la attende invece di crearne un'altra.
  const earlyCreatePromiseRef = useRef(null);
  const earlyCreate = (cfg) => {
    earlyCreatePromiseRef.current = (async () => {
      try {
        const { bot, sourcesError } = await performCreate(cfg);
        clearDraft();
        onIntroConsumed();
        setSourcesWarning(sourcesError);
        setCreatedBot(bot);
        return bot;
      } catch (error) {
        console.error(
          "Creazione anticipata fallita (si ritenta alla conferma):",
          error
        );
        return null;
      }
    })();
    return earlyCreatePromiseRef.current;
  };

  const mapCreateError = (error) =>
    describeApiError(error, t) || t("onboardingWizard.createErrorGeneric");

  // --- Review v2: "Fonti di conoscenza" ---------------------------------
  const setRowStatus = (id, status) =>
    setAvailableSources((rows) =>
      rows.map((r) => (r.id === id ? { ...r, status } : r))
    );

  // Aggiunge una fonte al bot: push immediato se il bot esiste (creazione
  // anticipata riuscita), altrimenti resta in coda in config.sources e parte
  // con la creazione alla "Pubblica". La riga passa a "added" e l'anteprima
  // viva inizia a usarla appena l'ingestione è completata.
  const addSourceFromReview = async (row) => {
    setAddError("");
    setRowStatus(row.id, "adding");
    try {
      let bot = createdBot;
      if (!bot && earlyCreatePromiseRef.current) {
        bot = await earlyCreatePromiseRef.current;
      }
      if (bot) {
        const createdDocs = await pushSources(bot._id, [row.source]);
        attachCreatedIds(createdDocs);
        createdSourcesRef.current = [
          ...createdSourcesRef.current,
          sourceKey(row.source),
        ];
      } else if (oltreIlPiano(config.sources.length + 1)) {
        // Assistente non ancora creato (la creazione anticipata non e'
        // riuscita): la fonte resterebbe in coda senza che nessuno controlli
        // il piano. Stesso conto dell'API, stessa risposta.
        const piano = consumi.data?.plan;
        setRowStatus(row.id, "available");
        if (propostaPiani) setFuoriPiano({ motivo: null });
        else
          setAddError(
            t("onboardingWizard.planSourcesFull", {
              limit: consumi.data?.effective?.limits?.sources_max,
              plan: !piano || piano.key === GRATUITO ? t("onboardingWizard.planFree") : piano.name,
            })
          );
        return;
      }
      setConfig((c) => ({ ...c, sources: [...c.sources, row.source] }));
      setRowStatus(row.id, "added");
    } catch (error) {
      console.error("Aggiunta fonte fallita:", error);
      setRowStatus(row.id, "available");
      // Piano pieno e piani da proporre: lo dice la scelta del piano che si
      // e' appena aperta, non serve anche la riga rossa.
      setAddError(
        eLimiteDelPiano(error) && propostaPiani
          ? ""
          : describeApiError(error, t) || t("onboardingWizard.kbAddError")
      );
    }
  };

  // Fonti nuove (dropzone o inserimento manuale): entrano in lista e si
  // aggiungono subito — le ha scelte esplicitamente l'utente.
  const addNewSources = (sources) => {
    const known = new Set([
      ...config.sources.map(sourceKey),
      ...availableSources.map((r) => sourceKey(r.source)),
    ]);
    const rows = sources
      .filter((source) => !known.has(sourceKey(source)))
      .map((source) => ({
        id: `new-${rowIdRef.current++}`,
        status: "available",
        source,
      }));
    if (rows.length === 0) return;
    setAvailableSources((prev) => [...prev, ...rows]);
    rows.forEach((row) => addSourceFromReview(row));
  };

  const handleReviewFiles = async (fileList) => {
    const errors = [];
    const added = [];
    for (const file of Array.from(fileList)) {
      if (file.size > REVIEW_MAX_FILE_BYTES) {
        errors.push(`${file.name}: ${t("onboardingWizard.fileTooLarge")}`);
        continue;
      }
      const ext = (file.name.split(".").pop() || "").toLowerCase();
      if (ext === "pdf") {
        added.push({ type: "pdf", file, filename: file.name });
      } else if (ext === "txt" || ext === "md") {
        const text = await file.text();
        if (text.trim().length < 10) {
          errors.push(`${file.name}: ${t("onboardingWizard.fileTooShort")}`);
          continue;
        }
        added.push({
          type: "text",
          title: file.name,
          inline_text: text.slice(0, REVIEW_MAX_INLINE_TEXT),
          fromFile: true,
          fileExt: ext,
          sizeBytes: file.size,
        });
      } else {
        errors.push(`${file.name}: ${t("onboardingWizard.docNotSupported")}`);
      }
    }
    setFileErrors(errors);
    if (added.length > 0) addNewSources(added);
  };


  // Ramo custom: loader di creazione → pagina success.
  // Se il bot esiste già (creazione anticipata + "Personalizza"), niente
  // duplicati: si aggiorna il bot e si aggiungono solo le fonti nuove.
  const handleCreate = async () => {
    setPhase("creating");
    setCreateError("");

    try {
      if (createdBot) {
        await api.bots.update(createdBot._id, {
          name: config.name.trim(),
          system_prompt: config.systemPrompt,
          widget_config: buildWidgetConfig(),
        });
        const newSources = config.sources.filter(
          (s) => !createdSourcesRef.current.includes(sourceKey(s))
        );
        try {
          const createdDocs = await pushSources(createdBot._id, newSources);
          attachCreatedIds(createdDocs);
          createdSourcesRef.current = config.sources.map(sourceKey);
          setSourcesWarning(null);
        } catch (error) {
          console.error("Fonti aggiuntive non aggiunte:", error);
          setSourcesWarning(
            describeApiError(error, t) ||
              t("onboardingWizard.sourcesFailedReason")
          );
        }
        clearDraft();
        // Nuovo oggetto per rinnovare il riferimento (preview/success).
        setCreatedBot({ ...createdBot });
        return;
      }

      const { bot, sourcesError } = await performCreate();
      clearDraft();
      onIntroConsumed();
      setSourcesWarning(sourcesError);
      // Resta sul loader finché la checklist non si completa: il passaggio
      // alla pagina success lo fa CreationLoader via onFinished.
      setCreatedBot(bot);
    } catch (error) {
      console.error("Creazione bot fallita:", error);
      setPhase("form");
      setCreateError(mapCreateError(error));
    }
  };

  // Ramo auto: "Conferma e vai alla dashboard" dal riepilogo. Si va
  // dritti alla Dashboard: i piani, se servivano, si sono gia' aperti
  // quando le fonti hanno superato il piano.
  const handleConfirm = async () => {
    // Il bot esiste già (creazione anticipata, o tentativo con sole fonti
    // fallite): non ricrearlo, prosegui e basta.
    if (createdBot) {
      onComplete(createdBot);
      return;
    }

    // Creazione anticipata ancora in volo: attendila invece di duplicare.
    if (earlyCreatePromiseRef.current) {
      setIsConfirming(true);
      const bot = await earlyCreatePromiseRef.current;
      setIsConfirming(false);
      if (bot) {
        onComplete(bot);
        return;
      }
      // Fallita: si prosegue con la creazione normale qui sotto.
    }

    setIsConfirming(true);
    setCreateError("");

    try {
      const { bot, sourcesError } = await performCreate();
      clearDraft();
      onIntroConsumed();
      if (sourcesError) {
        // Bot creato ma fonti no: fermati e dillo, invece di portare l'utente
        // in dashboard convinto che sia andato tutto bene.
        setCreatedBot(bot);
        setSourcesWarning(sourcesError);
        setIsConfirming(false);
        return;
      }
      onComplete(bot);
    } catch (error) {
      console.error("Creazione bot fallita:", error);
      setIsConfirming(false);
      setCreateError(mapCreateError(error));
    }
  };

  // Mappa il risultato dell'analisi (sito o prompt) nella config del wizard
  // (stessa forma dei campi degli step custom → riusabile ovunque).
  // extraSources: file allegati dalla entry; contextNote: descrizione
  // dell'attività da accodare al system prompt come contesto.
  const applyAnalysis = (data, { extraSources = [], contextNote = "" } = {}) => {
    const basePrompt = (data.systemPrompt || "").trim();
    const systemPrompt = contextNote
      ? `${basePrompt}\n\nContesto aggiuntivo:\n${contextNote}`.slice(0, 2000)
      : basePrompt.slice(0, 2000);
    const patch = {
      name: data.botName || "",
      subtitle: data.widgetConfig?.subtitle || "",
      welcomeMessage: data.widgetConfig?.welcomeMessage || "",
      logoUrl: data.widgetConfig?.theme?.logoUrl || "",
      primaryColor:
        data.widgetConfig?.theme?.primaryColor || DEFAULT_CONFIG.primaryColor,
      secondaryColor:
        data.widgetConfig?.theme?.secondaryColor ||
        DEFAULT_CONFIG.secondaryColor,
      textColor:
        data.widgetConfig?.theme?.textColor || DEFAULT_CONFIG.textColor,
      systemPrompt,
      promptMode: "custom",
      quickReplies:
        (data.widgetConfig?.behavior?.quickReplies || []).slice(0, 5),
      // Fonti auto-generate dall'analisi (testo/faq per il prompt, homepage
      // + pagine rilevanti per il sito) partono già "aggiunte"; il resto
      // (link scoperti ma non auto-selezionati, es. dalla sitemap) va in
      // "Fonti disponibili" e lo sceglie l'utente col "+".
      sources: [...(data.autoSources || []), ...extraSources],
    };
    updateConfig(patch);
    setAvailableSources([
      ...(data.suggestedSources || []).map((s) => ({
        id: `det-${rowIdRef.current++}`,
        status: "available",
        source: {
          type: s.type === "sitemap" ? "sitemap" : "url",
          url: s.url,
        },
      })),
      ...(data.autoSources || []).map((source) => ({
        id: `auto-${rowIdRef.current++}`,
        status: "added",
        source,
      })),
      ...extraSources.map((source) => ({
        id: `intro-${rowIdRef.current++}`,
        status: "added",
        source,
      })),
    ]);
    // Il patch torna al chiamante: serve alla creazione anticipata, che
    // avviene nello stesso tick (lo stato config non è ancora aggiornato).
    return patch;
  };

  // File allegati esplicitamente dall'utente nella entry (landing page).
  // Il racconto testuale NON si auto-aggiunge più come fonte grezza: per il
  // ramo prompt ci pensa la base di conoscenza generata dall'AI (autoSources
  // di analyzeFromPrompt), sempre grounded sullo stesso racconto passato
  // come contextNote al system prompt.
  const introSources = () => introFiles;

  // Ramo auto: "Analizza sito web" → loader → riepilogo (o pagina errore).
  // urlOverride: usato dall'avvio automatico della entry (lo stato config
  // non è ancora aggiornato in quel tick).
  // Mappa gli eventi reali del backend (crawl/identity/links) sugli step.
  const bumpWebsiteProgress = (step) =>
    setAnalysisProgress((p) => Math.max(p, WEBSITE_PROGRESS_OF[step] || 0));

  const handleAnalyze = async (urlOverride) => {
    setPhase("analyzing");
    setAnalysisError(null);
    setAnalysis(null);
    setAnalysisProgress(0);

    try {
      const raw = typeof urlOverride === "string" && urlOverride
        ? urlOverride
        : config.websiteUrl;
      const cleaned = raw.trim().replace(/^https?:\/\//i, "");
      // Streaming SSE: ogni fase reale (crawl/identity/links) spunta uno step.
      const data = await api.bots.analyzeStream(
        "/bots/analyze-website-stream",
        { websiteUrl: `https://${cleaned}` },
        bumpWebsiteProgress
      );
      const patch = applyAnalysis(data, {
        extraSources: introSources(),
        contextNote: intro?.description || "",
      });
      setAnalysis(data);
      setDiscoveredLinks(data.discoveredLinks || []);
      // Bot creato subito (senza attendere la Conferma): l'ultimo step della
      // checklist si spunta SOLO quando il bot esiste davvero.
      earlyCreate({ ...config, ...patch }).finally(() =>
        setAnalysisProgress(WEBSITE_ANALYSIS_STEPS.length)
      );
    } catch (error) {
      console.error("Analisi sito fallita:", error);
      // Pagina errore dedicata (design 4060:1206) con la causa specifica:
      // sito irraggiungibile/bloccato, contenuto illeggibile, AI in errore...
      setAnalysisError(describeAnalysisError(error, t));
      setPhase("analysisError");
    }
  };

  // Ramo prompt (v2): bozza dell'assistente dal solo racconto dell'attività.
  // Stessa forma dell'analisi sito → stesso loader e stesso riepilogo.
  const bumpPromptProgress = (step) =>
    setAnalysisProgress((p) => Math.max(p, PROMPT_PROGRESS_OF[step] || 0));

  const handleAnalyzePrompt = async () => {
    setPhase("analyzing");
    setAnalysisError(null);
    setAnalysis(null);
    setAnalysisProgress(0);

    try {
      const data = await api.bots.analyzeStream(
        "/bots/analyze-prompt-stream",
        { description: intro?.description || "" },
        bumpPromptProgress
      );
      const patch = applyAnalysis(data, { extraSources: introSources() });
      setAnalysis(data);
      // Niente sito da leggere in questo ramo: nessun link da proporre.
      setDiscoveredLinks([]);
      earlyCreate({ ...config, ...patch }).finally(() =>
        setAnalysisProgress(PROMPT_ANALYSIS_STEPS.length)
      );
    } catch (error) {
      console.error("Bozza da prompt fallita:", error);
      setAnalysisError(describeAnalysisError(error, t));
      setPhase("analysisError");
    }
  };

  // Avvio automatico dalla entry prompt-first: con sito → analisi del sito
  // (arricchita dal racconto); senza sito → bozza dal solo prompt.
  useEffect(() => {
    if (!intro || phase !== "form") return;
    const signature = `${initialMode}|${intro.description || ""}|${intro.websiteUrl || ""}`;
    if (introRunGuard === signature) return; // remount StrictMode / doppio mount
    if (initialMode === "prompt" && intro.description) {
      introRunGuard = signature;
      handleAnalyzePrompt();
    } else if (initialMode === "auto" && intro.websiteUrl) {
      introRunGuard = signature;
      updateConfig({ websiteUrl: intro.websiteUrl });
      handleAnalyze(intro.websiteUrl);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const steps = [
    { key: "personalization", label: t("onboardingWizard.stepPersonalization") },
    { key: "settings", label: t("onboardingWizard.stepSettings") },
    { key: "sources", label: t("onboardingWizard.stepSources") },
  ];

  // La scelta del piano, sopra il riepilogo o le fonti: un piano porta alla
  // pagina Abbonamento (l'assistente, se c'e', e' gia' salvato; la bozza
  // resta), il gratuito chiude e si continua.
  const sceltaPiano =
    fuoriPiano && propostaPiani ? (
      <ModalePiani
        proposta={propostaPiani}
        motivo={fuoriPiano.motivo}
        onScegli={(path) => onComplete(createdBot, path)}
        onGratuito={() => setFuoriPiano(null)}
      />
    ) : null;

  // Fasi speciali (design Figma: loader 1/2, riepilogo auto, success custom).
  if (phase === "analyzing") {
    return (
      <CreationLoader
        done={!!analysis}
        progress={analysisProgress}
        steps={initialMode === "prompt" ? PROMPT_ANALYSIS_STEPS : WEBSITE_ANALYSIS_STEPS}
        onFinished={() => setPhase("review")}
      />
    );
  }
  if (phase === "analysisError" && analysisError) {
    return (
      <AnalysisErrorPage
        error={analysisError}
        onRetry={
          initialMode === "prompt"
            ? () => handleAnalyzePrompt()
            : () => setPhase("form")
        }
        onSwitchToCustom={() => {
          setBranch("custom");
          setStep(1);
          setPhase("form");
        }}
      />
    );
  }
  if (phase === "review" && analysis) {
    return (
      <>
        {sceltaPiano}
        <AnalysisReview
          config={config}
          previewConfig={previewConfig}
          websiteUrl={config.websiteUrl}
          fromPrompt={initialMode === "prompt"}
          sources={availableSources}
          discoveredLinks={discoveredLinks}
          onRemoveSource={removeSourceFromReview}
          onAddFiles={handleReviewFiles}
          onAddManual={(sourceOrSources) =>
            addNewSources(
              Array.isArray(sourceOrSources) ? sourceOrSources : [sourceOrSources]
            )
          }
          fileErrors={fileErrors}
          addError={addError}
          isPublishing={isConfirming}
          createError={createError}
          sourcesWarning={sourcesWarning}
          onCustomize={() => {
            setBranch("custom");
            setStep(1);
            setPhase("form");
          }}
          onPublish={handleConfirm}
        />
      </>
    );
  }
  if (phase === "creating") {
    return (
      <CreationLoader
        done={!!createdBot}
        onFinished={() => setPhase("success")}
      />
    );
  }
  if (phase === "success" && createdBot) {
    return (
      <CreationSuccess
        bot={createdBot}
        sourcesWarning={sourcesWarning}
        onNavigate={(path) => onComplete(createdBot, path)}
      />
    );
  }

  // Ramo prompt in attesa dell'avvio automatico: niente schermata URL,
  // si vede direttamente il loader (l'effect parte subito dopo il mount).
  if (initialMode === "prompt" && branch === "auto") {
    return (
      <CreationLoader
        done={!!analysis}
        progress={analysisProgress}
        steps={PROMPT_ANALYSIS_STEPS}
        onFinished={() => setPhase("review")}
      />
    );
  }

  // Ramo automatico: schermata di inserimento URL (senza stepper).
  if (branch === "auto") {
    return (
      <div className="pb-10">
        <StepWebsite
          url={config.websiteUrl}
          onUrlChange={(value) => updateConfig({ websiteUrl: value })}
          onBack={() => {
            clearDraft();
            if (onExit) onExit();
          }}
          onAnalyze={handleAnalyze}
          onSwitchToCustom={() => {
            setBranch("custom");
            setStep(1);
          }}
        />
      </div>
    );
  }

  // La Live Preview affianca gli step 1 e 2 (design); lo step sorgenti è a tutta larghezza
  const showPreview = step === 1 || step === 2;

  return (
    <div className="px-4 py-10">
      {sceltaPiano}
      <WizardStepper steps={steps} current={step} />
      <div className="mx-auto mt-10 flex max-w-[1040px] flex-col justify-center gap-8 lg:flex-row lg:items-start">
        <div className="w-full lg:max-w-[620px]">
          {step === 1 && (
            <StepPersonalization
              config={config}
              updateConfig={updateConfig}
              onBack={() => {
                clearDraft();
                if (onExit) onExit();
              }}
              onNext={() => setStep(2)}
            />
          )}
          {step === 2 && (
            <StepSettings
              config={config}
              updateConfig={updateConfig}
              onBack={() => setStep(1)}
              onNext={() => setStep(3)}
            />
          )}
          {step === 3 && (
            <StepSources
              config={config}
              updateConfig={updateConfig}
              onBack={() => setStep(2)}
              onCreate={handleCreate}
              isCreating={isCreating}
              createError={createError}
              onOltreIlPiano={propostaPiani ? () => setFuoriPiano({ motivo: null }) : undefined}
            />
          )}
        </div>

        {showPreview && (
          <aside className="w-full shrink-0 lg:w-[360px]">
            <h3 className="text-sm font-semibold">
              {t("onboardingWizard.livePreview")}
            </h3>
            <p className="mt-1 text-xs leading-relaxed text-muted-foreground">
              {t("onboardingWizard.livePreviewHint")}
            </p>
            {/* Widget all'80% (448/560): il wrapper interno è ancorato in
                basso e più alto del box visibile, così lo spazio vuoto
                sopra la chat resta tagliato fuori. */}
            <div className="relative mt-4 h-[370px] overflow-hidden rounded-lg">
              <div className="absolute bottom-0 left-0 right-0 h-[448px]">
                <WidgetPreview config={previewConfig} desktopVH={560} />
              </div>
            </div>
          </aside>
        )}
      </div>
    </div>
  );
};

export default CustomBotWizard;
